import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowReset20Regular,
  Dismiss20Regular,
  ZoomIn20Regular,
  ZoomOut20Regular,
} from '@fluentui/react-icons';

/* ------------------------------------------------------------------ */
/*  DiagramViewer                                                      */
/*                                                                     */
/*  Full-screen view of a rendered Mermaid diagram. A wide tree shrunk */
/*  to the width of a chat bubble is unreadable: here it opens fitted  */
/*  to the screen, then zooms (wheel, buttons, + / -) and pans (drag). */
/* ------------------------------------------------------------------ */

const MIN_SCALE = 0.1;
const MAX_SCALE = 8;
const STEP = 1.25;

/** Natural size of a Mermaid SVG, read from its viewBox. */
const svgSize = (svg: string): { w: number; h: number } => {
  const m = /viewBox="[-\d.]+\s+[-\d.]+\s+([\d.]+)\s+([\d.]+)"/.exec(svg);
  return m ? { w: parseFloat(m[1]), h: parseFloat(m[2]) } : { w: 800, h: 600 };
};

const clamp = (v: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, v));

interface DiagramViewerProps {
  /** SVG markup produced by mermaid (securityLevel 'strict'). */
  svg: string;
  title?: string;
  onClose: () => void;
}

const DiagramViewer: React.FC<DiagramViewerProps> = ({ svg, title = 'Schéma', onClose }) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  // One state for scale and position: a zoom moves both, and the updater must stay
  // pure (StrictMode runs it twice)
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  const { w, h } = svgSize(svg);

  const fit = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const { clientWidth: sw, clientHeight: sh } = stage;
    const s = clamp(Math.min(sw / w, sh / h) * 0.94);
    setView({ s, x: (sw - w * s) / 2, y: (sh - h * s) / 2 });
  }, [w, h]);

  /** Zoom by `factor`, keeping the point under (cx, cy) where it is. */
  const zoomAt = useCallback((factor: number, cx?: number, cy?: number) => {
    const stage = stageRef.current;
    if (!stage) return;
    const px = cx ?? stage.clientWidth / 2;
    const py = cy ?? stage.clientHeight / 2;
    setView((v) => {
      const s = clamp(v.s * factor);
      const ratio = s / v.s;
      return { s, x: px - (px - v.x) * ratio, y: py - (py - v.y) * ratio };
    });
  }, []);

  useLayoutEffect(fit, [fit]);

  // Wheel must be non-passive to stop the page from scrolling underneath
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = stage.getBoundingClientRect();
      zoomAt(e.deltaY < 0 ? 1.1 : 1 / 1.1, e.clientX - rect.left, e.clientY - rect.top);
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === '+' || e.key === '=') zoomAt(STEP);
      else if (e.key === '-') zoomAt(1 / STEP);
      else if (e.key === '0') fit();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', fit);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', fit);
    };
  }, [onClose, zoomAt, fit]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, ox: view.x, oy: view.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (d) setView((v) => ({ ...v, x: d.ox + e.clientX - d.x, y: d.oy + e.clientY - d.y }));
  };
  const endDrag = () => {
    drag.current = null;
  };

  return createPortal(
    <div className="dv-backdrop" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div className="dv-panel" onClick={(e) => e.stopPropagation()}>
        <div className="dv-toolbar">
          <span className="dv-title">{title}</span>
          <button type="button" onClick={() => zoomAt(1 / STEP)} title="Dézoomer (−)">
            <ZoomOut20Regular />
          </button>
          <span className="dv-scale">{Math.round(view.s * 100)} %</span>
          <button type="button" onClick={() => zoomAt(STEP)} title="Zoomer (+)">
            <ZoomIn20Regular />
          </button>
          <button type="button" onClick={fit} title="Ajuster à l'écran (0)">
            <ArrowReset20Regular />
          </button>
          <button type="button" className="dv-close" onClick={onClose} title="Fermer (Échap)">
            <Dismiss20Regular />
          </button>
        </div>
        <div
          ref={stageRef}
          className="dv-stage"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <div
            className="dv-content"
            style={{
              width: w,
              height: h,
              transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})`,
            }}
            // Same mermaid output as the inline diagram (strict mode, labels escaped)
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        </div>
        <div className="dv-hint">Molette pour zoomer · glisser pour déplacer · Échap pour fermer</div>
      </div>
    </div>,
    document.body
  );
};

export default DiagramViewer;
